import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import ProtectedRoute from './ProtectedRoute';
import Layout from '../components/Common/Layout';
import useAuth from '../hooks/useAuth';

const RootRedirect = () => {
  const { isSuperAdmin, user } = useAuth();
  const target = user?.role === 'Customer' ? '/customer-portal' : isSuperAdmin ? '/super-admin' : '/dashboard';
  return <Navigate to={target} replace />;
};

// Keeps portal accounts (role: 'Customer') out of the internal ERP shell —
// they only ever land in /customer-portal/*.
const StaffGuard = ({ children }) => {
  const { user } = useAuth();
  if (user?.role === 'Customer') return <Navigate to="/customer-portal" replace />;
  return children;
};

const lazy = (fn) => React.lazy(fn);

// Auth / Landing
const LandingPage             = lazy(() => import('../pages/Landing/LandingPage'));
const MarketingProductsPage   = lazy(() => import('../pages/Landing/ProductsPage'));
const MarketingPricingPage    = lazy(() => import('../pages/Landing/PricingPage'));
const MarketingPartnersPage   = lazy(() => import('../pages/Landing/PartnersPage'));
const BookDemoPage            = lazy(() => import('../pages/Landing/BookDemoPage'));
const ThankYouPage            = lazy(() => import('../pages/Landing/ThankYouPage'));
const TermsPage               = lazy(() => import('../pages/Legal/TermsPage'));
const PrivacyPage             = lazy(() => import('../pages/Legal/PrivacyPage'));
const RefundPage              = lazy(() => import('../pages/Legal/RefundPage'));
const LoginPage               = lazy(() => import('../pages/Auth/LoginPage'));
const RegisterPage            = lazy(() => import('../pages/Auth/RegisterPage'));
const ForgotPasswordPage      = lazy(() => import('../pages/Auth/ForgotPasswordPage'));
const ResetPasswordPage       = lazy(() => import('../pages/Auth/ResetPasswordPage'));
const SSOCallbackPage         = lazy(() => import('../pages/Auth/SSOCallbackPage'));
const ProfilePage             = lazy(() => import('../pages/Auth/ProfilePage'));

// Dashboard
const DashboardPage           = lazy(() => import('../pages/Dashboard/DashboardPage'));

// Products
const ProductListPage         = lazy(() => import('../pages/Products/ProductListPage'));
const ProductDetailsPage      = lazy(() => import('../pages/Products/ProductDetailsPage'));
const ProductFormPage         = lazy(() => import('../pages/Products/ProductFormPage'));

// Inventory
const InventoryDashboardPage  = lazy(() => import('../pages/Inventory/InventoryDashboardPage'));
const StockAdjustmentPage     = lazy(() => import('../pages/Inventory/StockAdjustmentPage'));
const StockTransferPage       = lazy(() => import('../pages/Inventory/StockTransferPage'));

// Suppliers
const SupplierListPage        = lazy(() => import('../pages/Suppliers/SupplierListPage'));
const SupplierFormPage        = lazy(() => import('../pages/Suppliers/SupplierFormPage'));

// Customers
const CustomerListPage        = lazy(() => import('../pages/Customers/CustomerListPage'));
const CustomerFormPage        = lazy(() => import('../pages/Customers/CustomerFormPage'));
const CustomerDetailsPage     = lazy(() => import('../pages/Customers/CustomerDetailsPage'));

// Plants
const PlantListPage           = lazy(() => import('../pages/Plants/PlantListPage'));

// RFQ
const RFQListPage             = lazy(() => import('../pages/RFQ/RFQListPage'));
const RFQFormPage             = lazy(() => import('../pages/RFQ/RFQFormPage'));
const RFQDetailsPage          = lazy(() => import('../pages/RFQ/RFQDetailsPage'));
const BidComparisonPage       = lazy(() => import('../pages/RFQ/BidComparisonPage'));

// Contracts
const ContractListPage        = lazy(() => import('../pages/Contracts/ContractListPage'));
const ContractFormPage        = lazy(() => import('../pages/Contracts/ContractFormPage'));
const ContractDetailsPage     = lazy(() => import('../pages/Contracts/ContractDetailsPage'));

// Quotations
const QuotationListPage       = lazy(() => import('../pages/Quotations/QuotationListPage'));
const QuotationFormPage       = lazy(() => import('../pages/Quotations/QuotationFormPage'));
const QuotationDetailsPage    = lazy(() => import('../pages/Quotations/QuotationDetailsPage'));

// Purchase Orders
const PurchaseOrderListPage   = lazy(() => import('../pages/Orders/PurchaseOrderListPage'));
const PurchaseOrderDetailsPage= lazy(() => import('../pages/Orders/PurchaseOrderDetailsPage'));
const PurchaseOrderFormPage   = lazy(() => import('../pages/Orders/PurchaseOrderFormPage'));

// Sales Orders
const SalesOrderListPage      = lazy(() => import('../pages/Orders/SalesOrderListPage'));
const SalesOrderDetailsPage   = lazy(() => import('../pages/Orders/SalesOrderDetailsPage'));
const SalesOrderFormPage      = lazy(() => import('../pages/Orders/SalesOrderFormPage'));

// Credit Notes
const CreditNoteListPage      = lazy(() => import('../pages/CreditNotes/CreditNoteListPage'));
const CreditNoteDetailsPage   = lazy(() => import('../pages/CreditNotes/CreditNoteDetailsPage'));
const CreditNoteFormPage      = lazy(() => import('../pages/CreditNotes/CreditNoteFormPage'));

// Debit Notes
const DebitNoteListPage       = lazy(() => import('../pages/DebitNotes/DebitNoteListPage'));
const DebitNoteDetailsPage    = lazy(() => import('../pages/DebitNotes/DebitNoteDetailsPage'));

// Goods Receipts
const GoodsReceiptListPage    = lazy(() => import('../pages/GoodsReceipts/GoodsReceiptListPage'));
const GoodsReceiptFormPage    = lazy(() => import('../pages/GoodsReceipts/GoodsReceiptFormPage'));
const GoodsReceiptDetailsPage = lazy(() => import('../pages/GoodsReceipts/GoodsReceiptDetailsPage'));

// Reconciliation
const ReconciliationListPage  = lazy(() => import('../pages/Reconciliation/ReconciliationListPage'));

// Returns
const ReturnsListPage  = lazy(() => import('../pages/Returns/ReturnsListPage'));

// Document Templates
const TemplateListPage    = lazy(() => import('../pages/DocumentTemplates/TemplateListPage'));
const TemplateBuilderPage = lazy(() => import('../pages/DocumentTemplates/TemplateBuilderPage'));

// Goods Issues
const GoodsIssueListPage      = lazy(() => import('../pages/GoodsIssues/GoodsIssueListPage'));
const GoodsIssueFormPage      = lazy(() => import('../pages/GoodsIssues/GoodsIssueFormPage'));
const GoodsIssueDetailsPage   = lazy(() => import('../pages/GoodsIssues/GoodsIssueDetailsPage'));
const EWayBillControlTower    = lazy(() => import('../pages/EWayBill/EWayBillControlTower'));

// Transfer Postings
const TransferPostingListPage   = lazy(() => import('../pages/TransferPostings/TransferPostingListPage'));
const TransferPostingFormPage   = lazy(() => import('../pages/TransferPostings/TransferPostingFormPage'));
const TransferPostingDetailsPage= lazy(() => import('../pages/TransferPostings/TransferPostingDetailsPage'));

// Physical Inventory
const PhysicalInventoryListPage   = lazy(() => import('../pages/PhysicalInventory/PhysicalInventoryListPage'));
const PhysicalInventoryFormPage   = lazy(() => import('../pages/PhysicalInventory/PhysicalInventoryFormPage'));
const PhysicalInventoryDetailsPage= lazy(() => import('../pages/PhysicalInventory/PhysicalInventoryDetailsPage'));

// Production
const ProductionListPage   = lazy(() => import('../pages/Production/ProductionListPage'));
const ProductionFormPage   = lazy(() => import('../pages/Production/ProductionFormPage'));


// Service Entry Sheet
const SESListPage          = lazy(() => import('../pages/ServiceEntrySheet/SESListPage'));
const SESFormPage          = lazy(() => import('../pages/ServiceEntrySheet/SESFormPage'));

// Consignment
const ConsignmentPage      = lazy(() => import('../pages/Consignment/ConsignmentPage'));

// Reports
const InventoryReportPage     = lazy(() => import('../pages/Reports/InventoryReportPage'));
const SalesReportPage         = lazy(() => import('../pages/Reports/SalesReportPage'));
const ProcurementReportPage   = lazy(() => import('../pages/Reports/ProcurementReportPage'));
const FastMovingReportPage    = lazy(() => import('../pages/Reports/FastMovingReportPage'));
const CategoryAnalysisReportPage = lazy(() => import('../pages/Reports/CategoryAnalysisReportPage'));

// Admin
const SuperAdminPanel         = lazy(() => import('../pages/Admin/SuperAdminPanel'));
const AdminPanel              = lazy(() => import('../pages/Admin/AdminPanel'));
const ApprovalRulesPage       = lazy(() => import('../pages/Admin/ApprovalRulesPage'));

// Approvals
const PendingApprovalsPage    = lazy(() => import('../pages/Approvals/PendingApprovalsPage'));

// Agents
const AgentInboxPage          = lazy(() => import('../pages/Agents/AgentInboxPage'));
const AgentHistoryPage        = lazy(() => import('../pages/Agents/AgentHistoryPage'));

// Billing
const SubscriptionPage        = lazy(() => import('../pages/Billing/SubscriptionPage'));

// Help
const HelpPage                = lazy(() => import('../pages/Help/HelpPage'));

// Supplier Portal
const SupplierPortalPage      = lazy(() => import('../pages/SupplierPortal/SupplierPortalPage'));
const SupplierPortalWrapper   = lazy(() => import('../pages/SupplierPortal/SupplierPortalWrapper'));

// Customer Portal
const CustomerPortalLayout           = lazy(() => import('../components/CustomerPortal/CustomerPortalLayout'));
const CustomerPortalDashboardPage    = lazy(() => import('../pages/CustomerPortal/CustomerPortalDashboardPage'));
const CustomerPortalSalesOrdersPage  = lazy(() => import('../pages/CustomerPortal/CustomerPortalSalesOrdersPage'));
const CustomerPortalSalesOrderDetailsPage = lazy(() => import('../pages/CustomerPortal/CustomerPortalSalesOrderDetailsPage'));
const CustomerPortalInvoicesPage     = lazy(() => import('../pages/CustomerPortal/CustomerPortalInvoicesPage'));
const CustomerPortalInvoiceDetailsPage = lazy(() => import('../pages/CustomerPortal/CustomerPortalInvoiceDetailsPage'));

const PageLoader = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
  </Box>
);

const AppRouter = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/platform" element={<MarketingProductsPage />} />
      <Route path="/pricing" element={<MarketingPricingPage />} />
      <Route path="/partners" element={<MarketingPartnersPage />} />
      <Route path="/book-demo" element={<BookDemoPage />} />
      <Route path="/thank-you" element={<ThankYouPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/refund-policy" element={<RefundPage />} />

      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<SSOCallbackPage />} />

      <Route path="/supplier-portal" element={<SupplierPortalPage />} />
      <Route path="/supplier-portal/:accessCode" element={<SupplierPortalPage />} />

      <Route path="/customer-portal" element={<PrivateRoute><CustomerPortalLayout /></PrivateRoute>}>
        <Route index element={<CustomerPortalDashboardPage />} />
        <Route path="sales-orders" element={<CustomerPortalSalesOrdersPage />} />
        <Route path="sales-orders/:id" element={<CustomerPortalSalesOrderDetailsPage />} />
        <Route path="invoices" element={<CustomerPortalInvoicesPage />} />
        <Route path="invoices/:id" element={<CustomerPortalInvoiceDetailsPage />} />
      </Route>

      <Route path="/*" element={
        <PrivateRoute>
          <StaffGuard>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/subscription" element={<SubscriptionPage />} />

                <Route path="/products" element={<ProtectedRoute path="/products"><ProductListPage /></ProtectedRoute>} />
                <Route path="/products/:id" element={<ProtectedRoute path="/products"><ProductDetailsPage /></ProtectedRoute>} />
                <Route path="/products/new" element={<ProtectedRoute path="/products"><ProductFormPage /></ProtectedRoute>} />
                <Route path="/products/edit/:id" element={<ProtectedRoute path="/products"><ProductFormPage /></ProtectedRoute>} />

                <Route path="/inventory" element={<ProtectedRoute path="/inventory"><InventoryDashboardPage /></ProtectedRoute>} />
                <Route path="/inventory/adjust" element={<ProtectedRoute path="/inventory"><StockAdjustmentPage /></ProtectedRoute>} />
                <Route path="/inventory/transfer" element={<ProtectedRoute path="/inventory"><StockTransferPage /></ProtectedRoute>} />

                <Route path="/suppliers" element={<ProtectedRoute path="/suppliers"><SupplierListPage /></ProtectedRoute>} />
                <Route path="/suppliers/new" element={<ProtectedRoute path="/suppliers"><SupplierFormPage /></ProtectedRoute>} />
                <Route path="/suppliers/edit/:id" element={<ProtectedRoute path="/suppliers"><SupplierFormPage /></ProtectedRoute>} />

                <Route path="/customers" element={<ProtectedRoute path="/customers"><CustomerListPage /></ProtectedRoute>} />
                <Route path="/customers/new" element={<ProtectedRoute path="/customers"><CustomerFormPage /></ProtectedRoute>} />
                <Route path="/customers/edit/:id" element={<ProtectedRoute path="/customers"><CustomerFormPage /></ProtectedRoute>} />
                <Route path="/customers/:id" element={<ProtectedRoute path="/customers"><CustomerDetailsPage /></ProtectedRoute>} />

                <Route path="/plants" element={<ProtectedRoute path="/plants"><PlantListPage /></ProtectedRoute>} />
                <Route path="/supplier-portal-manage" element={<ProtectedRoute path="/supplier-portal-manage"><SupplierPortalWrapper /></ProtectedRoute>} />

                <Route path="/rfqs" element={<ProtectedRoute path="/rfqs"><RFQListPage /></ProtectedRoute>} />
                <Route path="/rfqs/new" element={<ProtectedRoute path="/rfqs"><RFQFormPage /></ProtectedRoute>} />
                <Route path="/rfqs/edit/:id" element={<ProtectedRoute path="/rfqs"><RFQFormPage /></ProtectedRoute>} />
                <Route path="/rfqs/:id" element={<ProtectedRoute path="/rfqs"><RFQDetailsPage /></ProtectedRoute>} />
                <Route path="/rfqs/:id/compare" element={<ProtectedRoute path="/rfqs"><BidComparisonPage /></ProtectedRoute>} />

                <Route path="/contracts" element={<ProtectedRoute path="/contracts"><ContractListPage /></ProtectedRoute>} />
                <Route path="/contracts/new" element={<ProtectedRoute path="/contracts"><ContractFormPage /></ProtectedRoute>} />
                <Route path="/contracts/edit/:id" element={<ProtectedRoute path="/contracts"><ContractFormPage /></ProtectedRoute>} />
                <Route path="/contracts/:id" element={<ProtectedRoute path="/contracts"><ContractDetailsPage /></ProtectedRoute>} />

                <Route path="/purchase-orders" element={<ProtectedRoute path="/purchase-orders"><PurchaseOrderListPage /></ProtectedRoute>} />
                <Route path="/purchase-orders/:id" element={<ProtectedRoute path="/purchase-orders"><PurchaseOrderDetailsPage /></ProtectedRoute>} />
                <Route path="/purchase-orders/new" element={<ProtectedRoute path="/purchase-orders"><PurchaseOrderFormPage /></ProtectedRoute>} />
                <Route path="/purchase-orders/edit/:id" element={<ProtectedRoute path="/purchase-orders"><PurchaseOrderFormPage /></ProtectedRoute>} />

                <Route path="/quotations" element={<ProtectedRoute path="/quotations"><QuotationListPage /></ProtectedRoute>} />
                <Route path="/quotations/new" element={<ProtectedRoute path="/quotations"><QuotationFormPage /></ProtectedRoute>} />
                <Route path="/quotations/edit/:id" element={<ProtectedRoute path="/quotations"><QuotationFormPage /></ProtectedRoute>} />
                <Route path="/quotations/:id" element={<ProtectedRoute path="/quotations"><QuotationDetailsPage /></ProtectedRoute>} />

                <Route path="/sales-orders" element={<ProtectedRoute path="/sales-orders"><SalesOrderListPage /></ProtectedRoute>} />
                <Route path="/sales-orders/:id" element={<ProtectedRoute path="/sales-orders"><SalesOrderDetailsPage /></ProtectedRoute>} />
                <Route path="/sales-orders/new" element={<ProtectedRoute path="/sales-orders"><SalesOrderFormPage /></ProtectedRoute>} />
                <Route path="/sales-orders/edit/:id" element={<ProtectedRoute path="/sales-orders"><SalesOrderFormPage /></ProtectedRoute>} />

                <Route path="/credit-notes" element={<ProtectedRoute path="/credit-notes"><CreditNoteListPage /></ProtectedRoute>} />
                <Route path="/credit-notes/new" element={<ProtectedRoute path="/credit-notes"><CreditNoteFormPage /></ProtectedRoute>} />
                <Route path="/credit-notes/edit/:id" element={<ProtectedRoute path="/credit-notes"><CreditNoteFormPage /></ProtectedRoute>} />
                <Route path="/credit-notes/:id" element={<ProtectedRoute path="/credit-notes"><CreditNoteDetailsPage /></ProtectedRoute>} />

                <Route path="/debit-notes" element={<ProtectedRoute path="/debit-notes"><DebitNoteListPage /></ProtectedRoute>} />
                <Route path="/debit-notes/:id" element={<ProtectedRoute path="/debit-notes"><DebitNoteDetailsPage /></ProtectedRoute>} />

                <Route path="/goods-receipts" element={<ProtectedRoute path="/goods-receipts"><GoodsReceiptListPage /></ProtectedRoute>} />
                <Route path="/goods-receipts/new" element={<ProtectedRoute path="/goods-receipts"><GoodsReceiptFormPage /></ProtectedRoute>} />
                <Route path="/goods-receipts/edit/:id" element={<ProtectedRoute path="/goods-receipts"><GoodsReceiptFormPage /></ProtectedRoute>} />
                <Route path="/goods-receipts/:id" element={<ProtectedRoute path="/goods-receipts"><GoodsReceiptDetailsPage /></ProtectedRoute>} />

                <Route path="/reconciliation" element={<ProtectedRoute path="/reconciliation"><ReconciliationListPage /></ProtectedRoute>} />
                <Route path="/returns" element={<ProtectedRoute path="/returns"><ReturnsListPage /></ProtectedRoute>} />

                <Route path="/document-templates" element={<ProtectedRoute path="/document-templates"><TemplateListPage /></ProtectedRoute>} />
                <Route path="/document-templates/:id" element={<ProtectedRoute path="/document-templates"><TemplateBuilderPage /></ProtectedRoute>} />

                <Route path="/goods-issues" element={<ProtectedRoute path="/goods-issues"><GoodsIssueListPage /></ProtectedRoute>} />
                <Route path="/goods-issues/new" element={<ProtectedRoute path="/goods-issues"><GoodsIssueFormPage /></ProtectedRoute>} />
                <Route path="/goods-issues/edit/:id" element={<ProtectedRoute path="/goods-issues"><GoodsIssueFormPage /></ProtectedRoute>} />
                <Route path="/goods-issues/:id" element={<ProtectedRoute path="/goods-issues"><GoodsIssueDetailsPage /></ProtectedRoute>} />
                <Route path="/eway-bills" element={<ProtectedRoute path="/goods-issues"><EWayBillControlTower /></ProtectedRoute>} />

                <Route path="/transfer-postings" element={<ProtectedRoute path="/transfer-postings"><TransferPostingListPage /></ProtectedRoute>} />
                <Route path="/transfer-postings/new" element={<ProtectedRoute path="/transfer-postings"><TransferPostingFormPage /></ProtectedRoute>} />
                <Route path="/transfer-postings/:id" element={<ProtectedRoute path="/transfer-postings"><TransferPostingDetailsPage /></ProtectedRoute>} />

                <Route path="/physical-inventory" element={<ProtectedRoute path="/physical-inventory"><PhysicalInventoryListPage /></ProtectedRoute>} />
                <Route path="/physical-inventory/new" element={<ProtectedRoute path="/physical-inventory"><PhysicalInventoryFormPage /></ProtectedRoute>} />
                <Route path="/physical-inventory/:id" element={<ProtectedRoute path="/physical-inventory"><PhysicalInventoryDetailsPage /></ProtectedRoute>} />

                <Route path="/production" element={<ProtectedRoute path="/production"><ProductionListPage /></ProtectedRoute>} />
                <Route path="/production/new" element={<ProtectedRoute path="/production"><ProductionFormPage /></ProtectedRoute>} />
                <Route path="/production/:id" element={<ProtectedRoute path="/production"><ProductionFormPage /></ProtectedRoute>} />


                <Route path="/ses" element={<ProtectedRoute path="/ses"><SESListPage /></ProtectedRoute>} />
                <Route path="/ses/new" element={<ProtectedRoute path="/ses"><SESFormPage /></ProtectedRoute>} />
                <Route path="/ses/:id" element={<ProtectedRoute path="/ses"><SESFormPage /></ProtectedRoute>} />

                <Route path="/consignment" element={<ProtectedRoute path="/consignment"><ConsignmentPage /></ProtectedRoute>} />

                <Route path="/reports/inventory" element={<ProtectedRoute path="/reports/inventory"><InventoryReportPage /></ProtectedRoute>} />
                <Route path="/reports/sales" element={<ProtectedRoute path="/reports/sales"><SalesReportPage /></ProtectedRoute>} />
                <Route path="/reports/procurement" element={<ProtectedRoute path="/reports/procurement"><ProcurementReportPage /></ProtectedRoute>} />
                <Route path="/reports/fast-moving" element={<ProtectedRoute path="/reports/fast-moving"><FastMovingReportPage /></ProtectedRoute>} />
                <Route path="/reports/category-analysis" element={<ProtectedRoute path="/reports/category-analysis"><CategoryAnalysisReportPage /></ProtectedRoute>} />

                <Route path="/help" element={<HelpPage />} />
                <Route path="/super-admin/*" element={<ProtectedRoute path="/super-admin"><SuperAdminPanel /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute path="/admin"><AdminPanel /></ProtectedRoute>} />
                <Route path="/admin/approval-rules" element={<ProtectedRoute path="/admin"><ApprovalRulesPage /></ProtectedRoute>} />

                <Route path="/approvals/pending" element={<PendingApprovalsPage />} />
                <Route path="/agents/inbox" element={<AgentInboxPage />} />
                <Route path="/agents/history" element={<AgentHistoryPage />} />
                <Route path="/" element={<RootRedirect />} />
              </Routes>
            </Suspense>
          </Layout>
          </StaffGuard>
        </PrivateRoute>
      } />
    </Routes>
  </Suspense>
);

export default AppRouter;
